import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import {
  fieldLocationPolicyProjectionV1Schema,
  fieldVisitTimeProjectionV1Schema,
  fieldWorkRelationshipV1Schema,
  fieldWorkShiftProjectionV1Schema,
} from "./field-projection-v1.js"
import {
  visitExecutionPlanProjectionV1Schema,
  visitExecutionSummaryV1Schema,
} from "./field-visit-execution-v1.js"

const localDateSchema = z.iso.date()
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/)

export const fieldVisitProjectionV2Schema = z.object({
  visitId: entityIdSchema,
  serviceOrderId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  startsAt: z.iso.datetime({ offset: true }),
  endsAt: z.iso.datetime({ offset: true }),
  timezone: z.string().min(1).max(64),
  state: z.literal("scheduled"),
  visitVersion: z.number().int().positive(),
  assignedResourceIds: z.array(entityIdSchema).max(25),
  executionPlan: visitExecutionPlanProjectionV1Schema,
  executionSummary: visitExecutionSummaryV1Schema,
}).strict().refine(
  (visit) => Date.parse(visit.startsAt) < Date.parse(visit.endsAt),
  {
    message: "Field Visit must end after it starts.",
    path: ["endsAt"],
  },
)

export const fieldProjectionV2Schema = z.object({
  responseVersion: z.literal(2),
  tenantId: tenantIdSchema,
  membershipId: membershipIdSchema,
  projectionVersion: sha256Schema,
  generatedAt: z.iso.datetime({ offset: true }),
  expiresAt: z.iso.datetime({ offset: true }),
  timezone: z.string().min(1).max(64),
  horizonStartDate: localDateSchema,
  horizonEndDate: localDateSchema,
  complete: z.boolean(),
  workerId: entityIdSchema,
  relationships: z.array(fieldWorkRelationshipV1Schema).max(20),
  locationPolicy: fieldLocationPolicyProjectionV1Schema.nullable(),
  workShift: fieldWorkShiftProjectionV1Schema.nullable(),
  visitTime: fieldVisitTimeProjectionV1Schema.nullable(),
  visits: z.array(fieldVisitProjectionV2Schema).max(100),
}).strict().superRefine((projection, context) => {
  const generated = Date.parse(projection.generatedAt)
  const expires = Date.parse(projection.expiresAt)
  if (expires <= generated || expires - generated > 15 * 60 * 1_000) {
    context.addIssue({
      code: "custom",
      message: "Field projection expiry must be within 15 minutes.",
      path: ["expiresAt"],
    })
  }
  const start = Date.parse(`${projection.horizonStartDate}T00:00:00Z`)
  const end = Date.parse(`${projection.horizonEndDate}T00:00:00Z`)
  if (end - start !== 2 * 24 * 60 * 60 * 1_000) {
    context.addIssue({
      code: "custom",
      message: "Field projection horizon must be today and the next day.",
      path: ["horizonEndDate"],
    })
  }
  if (
    new Set(projection.visits.map((visit) => visit.visitId)).size !==
    projection.visits.length
  ) {
    context.addIssue({
      code: "custom",
      message: "Field projection Visits must be unique.",
      path: ["visits"],
    })
  }
  if (
    projection.workShift &&
    !projection.relationships.some(
      (relationship) =>
        relationship.relationshipId === projection.workShift?.relationshipId &&
        relationship.legalEntityId === projection.workShift.legalEntityId,
    )
  ) {
    context.addIssue({
      code: "custom",
      message: "Active Work Shift must use a current relationship.",
      path: ["workShift"],
    })
  }
  if (
    projection.visitTime &&
    (!projection.workShift ||
      projection.visitTime.workShiftId !== projection.workShift.workShiftId)
  ) {
    context.addIssue({
      code: "custom",
      message: "Visit Time must be nested in the active Work Shift.",
      path: ["visitTime"],
    })
  }
})

export type FieldProjectionV2 = z.infer<typeof fieldProjectionV2Schema>
export type FieldVisitProjectionV2 = z.infer<typeof fieldVisitProjectionV2Schema>
